import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSessionContext } from '@/contexts/SessionContext';
import { useAuth } from '@/hooks/useAuth';

export interface PostMetrics {
  viewCount: number;
  commentCount: number;
  likeCount: number;
  isLiked: boolean;
  hasNewActivity: boolean;
  newCommentCount: number;
}

export type PostMetricsMap = Map<string, PostMetrics>;

const defaultMetrics: PostMetrics = {
  viewCount: 0,
  commentCount: 0,
  likeCount: 0,
  isLiked: false,
  hasNewActivity: false,
  newCommentCount: 0,
};

export const usePostMetrics = (postIds: string[]) => {
  const [metricsMap, setMetricsMap] = useState<PostMetricsMap>(new Map());
  const [loading, setLoading] = useState(true);
  const { sessionKey } = useSessionContext();
  const { user } = useAuth();

  // Memoize postIds to prevent unnecessary refetches
  const postIdsKey = useMemo(() => postIds.sort().join(','), [postIds]);
  const shouldTrackActivity = Boolean(user?.id);

  const fetchMetrics = useCallback(async () => {
    if (postIds.length === 0) {
      setMetricsMap(new Map());
      setLoading(false);
      return;
    }

    try {
      // Fetch all metrics in parallel with batch queries
      const [viewsResult, commentsResult, likesResult, userLikesResult, lastViewsResult, commentsWithTimesResult] = await Promise.all([
        // Batch view counts via RPC (type assertion needed as staging DB has this function)
        (supabase.rpc('get_post_view_counts' as any, { post_ids: postIds }) as unknown) as Promise<{ data: { blog_post_id: string; view_count: number }[] | null; error: any }>,
        
        // Batch comment counts via RPC (type assertion needed as staging DB has this function)
        (supabase.rpc('get_post_comment_counts' as any, { post_ids: postIds }) as unknown) as Promise<{ data: { blog_post_id: string; comment_count: number }[] | null; error: any }>,
        
        // Batch like counts
        supabase
          .from('likes')
          .select('likeable_id')
          .in('likeable_id', postIds)
          .eq('likeable_type', 'post'),
        
        // User's likes (to check isLiked)
        (user?.id || sessionKey) ? supabase
          .from('likes')
          .select('likeable_id')
          .in('likeable_id', postIds)
          .eq('likeable_type', 'post')
          .eq(user?.id ? 'user_id' : 'session_key', user?.id || sessionKey)
          : Promise.resolve({ data: [], error: null }),
        
        // Batch fetch user's last view time per post (for activity calculation)
        shouldTrackActivity ? supabase
          .from('views')
          .select('blog_post_id, created_at')
          .in('blog_post_id', postIds)
          .eq('user_id', user?.id)
          : Promise.resolve({ data: [], error: null }),
        
        // Batch fetch comments with timestamps via RPC for activity calculation
        shouldTrackActivity
          ? ((supabase.rpc('get_public_comments') as unknown) as Promise<{ data: { blog_post_id: string; created_at: string }[] | null; error: any }>)
          : Promise.resolve({ data: [], error: null }),
      ]);

      // Process results into counts per post
      const viewCounts = new Map<string, number>();
      const commentCounts = new Map<string, number>();
      const likeCounts = new Map<string, number>();
      const userLikedPosts = new Set<string>();

      // Use pre-aggregated view counts from RPC
      viewsResult.data?.forEach((v: any) => {
        viewCounts.set(v.blog_post_id, Number(v.view_count));
      });

      // Use pre-aggregated comment counts from RPC
      commentsResult.data?.forEach((c: any) => {
        commentCounts.set(c.blog_post_id, Number(c.comment_count));
      });

      // Count likes per post
      likesResult.data?.forEach((l: any) => {
        likeCounts.set(l.likeable_id, (likeCounts.get(l.likeable_id) || 0) + 1);
      });

      // Track user's liked posts
      userLikesResult.data?.forEach((l: any) => {
        userLikedPosts.add(l.likeable_id);
      });

      // Calculate activity status client-side using last views and comments
      const lastViewPerPost = new Map<string, Date>();
      lastViewsResult.data?.forEach((v: any) => {
        const existing = lastViewPerPost.get(v.blog_post_id);
        const viewTime = new Date(v.created_at);
        if (!existing || viewTime > existing) {
          lastViewPerPost.set(v.blog_post_id, viewTime);
        }
      });

      // Group comments by post with timestamps (filter to only posts we care about)
      const commentsPerPost = new Map<string, Date[]>();
      commentsWithTimesResult.data?.forEach((c: any) => {
        if (postIds.includes(c.blog_post_id)) {
          const postComments = commentsPerPost.get(c.blog_post_id) || [];
          postComments.push(new Date(c.created_at));
          commentsPerPost.set(c.blog_post_id, postComments);
        }
      });

      // Calculate new activity for each post
      const activityData = new Map<string, { hasNewActivity: boolean; newCommentCount: number }>();
      postIds.forEach((postId) => {
        if (!shouldTrackActivity) {
          activityData.set(postId, {
            hasNewActivity: false,
            newCommentCount: 0,
          });
          return;
        }

        const lastView = lastViewPerPost.get(postId) || new Date(0);
        const postComments = commentsPerPost.get(postId) || [];
        const newComments = postComments.filter(commentTime => commentTime > lastView);
        activityData.set(postId, {
          hasNewActivity: newComments.length > 0,
          newCommentCount: newComments.length,
        });
      });

      // Build final metrics map
      const newMetricsMap = new Map<string, PostMetrics>();
      postIds.forEach((postId) => {
        const activity = activityData.get(postId);
        newMetricsMap.set(postId, {
          viewCount: viewCounts.get(postId) || 0,
          commentCount: commentCounts.get(postId) || 0,
          likeCount: likeCounts.get(postId) || 0,
          isLiked: userLikedPosts.has(postId),
          hasNewActivity: activity?.hasNewActivity || false,
          newCommentCount: activity?.newCommentCount || 0,
        });
      });

      setMetricsMap(newMetricsMap);
    } catch (error) {
      console.error('Error fetching post metrics:', error);
      // Set default metrics on error
      const fallbackMap = new Map<string, PostMetrics>();
      postIds.forEach((postId) => fallbackMap.set(postId, { ...defaultMetrics }));
      setMetricsMap(fallbackMap);
    } finally {
      setLoading(false);
    }
  }, [postIdsKey, user?.id, sessionKey]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const getMetrics = useCallback((postId: string): PostMetrics => {
    return metricsMap.get(postId) || { ...defaultMetrics };
  }, [metricsMap]);

  return {
    metricsMap,
    getMetrics,
    loading,
    refetch: fetchMetrics,
  };
};
